import { createSelector } from "reselect";

export const getSearchState = state => state.search;

export const getResults = createSelector(getSearchState, state => state.results);

export const getTerm = createSelector(getSearchState, state => state.term);

export const getCenter = createSelector(getSearchState, state => state.center);

export const getAddress = createSelector(getSearchState, state => state.address);

export const isLoading = createSelector(getSearchState, state => state.isLoading);

export const getFlexibleLocation = createSelector(
  getSearchState,
  state => state.flexibleLocation === true,
);

export const getListView = createSelector(getSearchState, state => state.listView === true);

export const getMetaData = createSelector(getSearchState, state => state.metaData);

export const getCoursesMetaData = createSelector(getSearchState, state => state.coursesMetaData);

export const getCoursesResults = createSelector(getSearchState, state => state.coursesResults);

export const getFilters = createSelector(getSearchState, state => state.filters);

const coordinatesOf = result => {
  const lat = parseFloat(result.lat);
  const lng = parseFloat(result.lng);

  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

// A provider result is its own provider; a course result carries the provider offering it.
const providerOf = result => (result.modelName === "Provider" ? result : result.provider);

const addressOf = result => {
  const { street, postalCode, city } = result.address || {};

  return { street, postalCode, city };
};

/**
 * Results the index holds no coordinates for, which are the offerings given online or at the
 * customer's own place. The map lists them beside itself rather than drawing them.
 */
export const getOnlineResults = createSelector(getResults, results =>
  (results || []).filter(result => !coordinatesOf(result) && providerOf(result)),
);

/**
 * Search results that carry coordinates, grouped into one entry per point so the map draws a
 * single marker per venue. Results without coordinates are listed separately instead.
 */
export const getMapLocations = createSelector(getResults, results => {
  const locations = new Map();

  (results || []).forEach(result => {
    const coordinates = coordinatesOf(result);
    const provider = providerOf(result);

    if (!coordinates || !provider) {
      return;
    }

    const key = `${coordinates.lat},${coordinates.lng}`;
    const location = locations.get(key) || {
      ...coordinates,
      ...addressOf(result),
      key,
      distance: result.distance,
      results: [],
      providers: [],
    };

    location.results.push(result);
    if (!location.providers.some(({ id }) => id === provider.id)) {
      location.providers.push(provider);
    }

    locations.set(key, location);
  });

  return [...locations.values()];
});
